import { useReduxState } from 'hooks';
import { DropdownSelectable } from 'components/common/dropdown';
import { EditorBlockTranslationReference } from 'components/editor';
import {
  defaultBlockData,
  EditorBlockConverter,
  EditorBlockCustomControls
} from 'components/editor/blocks';
import { HeadingEditor } from 'components/editor/inputs';

import type { BlockHeading } from 'shared-types';
import type {
  BlockProps,
  EditorStateProps
} from 'components/editor/blocks/EditorBlock';

type StateProps = Pick<
  EditorStateProps,
  'editorLanguageActive' | 'editorLanguageReference'
>;

const EditorBlockHeading = ({
  blockId,
  data,
  isAIPreview,
  isPreview,
  languageVersion,
  updateData,
  ...stateProps
}: BlockProps<BlockHeading> & StateProps) => {
  const { messages, theme } = useReduxState();

  const { editorLanguageActive, editorLanguageReference } = stateProps;

  const handleChange = (newText: string) => {
    updateData({
      ...data,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...data.locales[languageVersion],
          translatable: {
            ...data.locales[languageVersion].translatable,
            text: newText
          }
        }
      }
    });
  };

  const handleLevelChange = (level: 'H2' | 'H3') => {
    updateData({ ...data, headingLevel: level });
  };

  const headingLevels = [
    { text: 'H2', value: 'H2' },
    { text: 'H3', value: 'H3' }
  ];

  return (
    <div className="heading">
      {isAIPreview || isPreview ? (
        data.headingLevel === 'H3' ? (
          <h3>{data.locales[languageVersion].translatable.text}</h3>
        ) : (
          <h2>{data.locales[languageVersion].translatable.text}</h2>
        )
      ) : (
        <>
          <EditorBlockTranslationReference
            referenceHtml={
              data.locales[editorLanguageReference]?.translatable?.text || ''
            }
            translationHtml={data.locales[languageVersion].translatable.text}
          >
            <HeadingEditor
              data={data}
              onChange={handleChange}
              text={data.locales[languageVersion].translatable.text || ''}
            />
          </EditorBlockTranslationReference>
        </>
      )}

      {!isAIPreview && !isPreview && !editorLanguageActive ? (
        <EditorBlockCustomControls>
          <DropdownSelectable
            align="left"
            category="lighter"
            defaultText={messages.editorBlockHeadingLevels}
            defaultValue={
              data.headingLevel || defaultBlockData.heading.headingLevel
            }
            onChange={handleLevelChange}
            options={headingLevels}
            selectedValueBefore={`${messages.editorBlockHeadingLevels}:`}
            showSelectedText={true}
            size="small"
            style={{ marginRight: 10 }}
          />
          <EditorBlockConverter
            blockId={blockId}
            blockType="heading"
            data={data}
            languageVersion={languageVersion}
          />
        </EditorBlockCustomControls>
      ) : null}

      <style jsx>{`
        .heading :global(h2),
        .heading :global(h3) {
          color: ${theme.textColor};
          font-size: ${data.headingLevel === 'H3' ? '1.8rem' : '2.2rem'};
          margin: 0;
        }
      `}</style>
    </div>
  );
};

export default EditorBlockHeading;
